import { useCallback } from 'react';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Area, Botao, Campo, Selecao } from '../componentes/forma.tsx';
import { Bancada, Chave, Lista, Painel } from '../componentes/painel.tsx';
import { ErroApi } from '../lib/api.ts';
import { useCadastro, useOpcoes } from '../lib/cadastro.ts';
import { dinheiro } from '../lib/formato.ts';
import { usePode } from '../lib/sessao.tsx';

interface Procedimento {
  id: number;
  nome: string;
  sigla: string | null;
  especialidadeId: number;
  valor: number | null;
  valorMedico: number | null;
  medicinaOcupacional: boolean;
  tipoLaudo: 'TEXTO' | 'OIT_2000';
  modeloLaudo: string | null;
  /** Aparece antes dos demais nas listas de escolha. */
  noTopo: boolean;
  ativo: boolean;
}

type Form = Omit<Procedimento, 'id'>;

const VAZIO: Form = {
  nome: '', sigla: '', especialidadeId: 0, valor: null, valorMedico: null,
  medicinaOcupacional: false, tipoLaudo: 'TEXTO', modeloLaudo: '',
  noTopo: false, ativo: true,
};

export function Procedimentos() {
  const pode = usePode();
  const podeEditar = pode('pode_procedimentos');
  const podeVerValores = pode('pode_financeiro');
  const opcoes = useOpcoes();

  const paraFormulario = useCallback((p: Procedimento) => {
    const { id: _id, ...resto } = p;
    return resto;
  }, []);

  const c = useCadastro<Procedimento, Form>({
    recurso: 'procedimentos',
    vazio: VAZIO,
    paraFormulario,
    rotulo: (p) => p.nome,
  });

  const nomeEspecialidade = (id: number) =>
    opcoes.data?.especialidades.find((e) => e.id === id)?.nome ?? '—';

  return (
    <Layout>
      <Cabecalho titulo="Procedimentos" contagem={c.lista.isPending ? '' : `${c.total}`}>
        {c.editando !== null && (
          <Botao onClick={c.limpar} variante="neutro">
            Novo procedimento
          </Botao>
        )}
      </Cabecalho>

      <Bancada>
        <Painel
          titulo={c.editando !== null ? 'Editar procedimento' : 'Novo procedimento'}
          editando={c.editando !== null}
          erro={c.erroGeral}
          salvo={c.salvo}
          salvando={c.salvando}
          rotuloCriar="Cadastrar procedimento"
          aoEnviar={c.enviar}
          aoCancelar={c.limpar}
          somenteLeitura={!podeEditar}
          excluindo={c.excluir.isPending}
          aoExcluir={() => {
            if (confirm(`Excluir ${c.form.nome}?`)) c.excluir.mutate(c.editando!);
          }}
        >
          <Campo
            rotulo="Nome"
            obrigatorio
            value={c.form.nome}
            erro={c.campos.nome}
            onChange={c.mudar('nome')}
            disabled={!podeEditar}
            dica="Aparece no laudo e no rodapé da imagem."
          />

          <Campo
            rotulo="Sigla"
            largura="curta"
            value={c.form.sigla ?? ''}
            erro={c.campos.sigla}
            onChange={c.mudar('sigla')}
            disabled={!podeEditar}
          />
          <Selecao
            rotulo="Especialidade"
            largura="meia"
            obrigatorio
            value={c.form.especialidadeId || ''}
            erro={c.campos.especialidadeId}
            onChange={(e) => c.definir('especialidadeId', Number(e.target.value))}
            disabled={!podeEditar}
          >
            <option value="">Selecione</option>
            {opcoes.data?.especialidades.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </Selecao>

          <Selecao
            rotulo="Tipo de laudo"
            largura="meia"
            value={c.form.tipoLaudo}
            erro={c.campos.tipoLaudo}
            onChange={c.mudar('tipoLaudo')}
            disabled={!podeEditar}
            dica={
              c.form.tipoLaudo === 'OIT_2000'
                ? 'Abre a folha OIT 2000 para o médico.'
                : 'Abre o editor de texto com o modelo abaixo.'
            }
          >
            <option value="TEXTO">Texto com modelo</option>
            <option value="OIT_2000">Folha OIT 2000</option>
          </Selecao>

          {podeVerValores && (
            <>
              <Campo
                rotulo="Valor cobrado"
                largura="meia"
                inputMode="numeric"
                placeholder="0,00"
                value={c.form.valor ?? ''}
                erro={c.campos.valor}
                onChange={c.mudar('valor')}
                disabled={!podeEditar}
              />
              {/* O "Valor do médico" saiu daqui na 028: o repasse passou a ser
                  uma taxa do médico, no cadastro dele, e não um atributo do
                  procedimento. O campo no banco ficou -- guarda o que a clínica
                  praticava antes --, mas deixar o campo na tela sem ninguém ler
                  seria pior que não ter: alguém o preencheria esperando efeito. */}
            </>
          )}

          {c.form.tipoLaudo === 'TEXTO' && (
            <Area
              rotulo="Modelo de laudo"
              rows={5}
              value={c.form.modeloLaudo ?? ''}
              erro={c.campos.modeloLaudo}
              onChange={c.mudar('modeloLaudo')}
              disabled={!podeEditar}
              dica="Texto que já vem preenchido para o médico revisar. Deixe em branco se não houver."
            />
          )}

          <Chave
            rotulo="Exame de medicina do trabalho"
            descricao="É o que a clínica de medicina ocupacional enxerga no portal dela. Procedimento não marcado não aparece lá."
            checked={c.form.medicinaOcupacional}
            onChange={c.mudar('medicinaOcupacional')}
            disabled={!podeEditar}
          />
          <Chave
            rotulo="Mostrar no início da lista"
            descricao="Sobe este exame para o topo das listas de escolha, no atendimento e nos relatórios. Para os poucos que respondem pela maior parte do dia — marcar muitos é o mesmo que não marcar nenhum."
            checked={c.form.noTopo}
            onChange={c.mudar('noTopo')}
            disabled={!podeEditar}
          />
          <Chave
            rotulo="Ativo"
            descricao="Procedimento desativado não aparece no cadastro de exames."
            checked={c.form.ativo}
            onChange={c.mudar('ativo')}
            disabled={!podeEditar}
          />
        </Painel>

        <div>
          <Lista
            carregando={c.lista.isPending}
            recarregando={c.lista.isFetching}
            erro={c.lista.error instanceof ErroApi ? c.lista.error.message : undefined}
            vazio={
              c.total === 0
                ? {
                    titulo: 'Nenhum procedimento cadastrado',
                    apoio: 'Cadastre os exames que a clínica realiza. Eles aparecem no cadastro do exame e definem o laudo.',
                  }
                : undefined
            }
          >
            <table className="tabela">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Sigla</th>
                  <th>Especialidade</th>
                  <th>Laudo</th>
                  {podeVerValores && <th className="tabela--num">Valor</th>}
                </tr>
              </thead>
              <tbody>
                {c.itens.map((p) => (
                  <tr
                    key={p.id}
                    tabIndex={0}
                    role="button"
                    data-selecionada={c.editando === p.id || undefined}
                    data-inativa={!p.ativo || undefined}
                    onClick={() => c.selecionar(p)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        c.selecionar(p);
                      }
                    }}
                  >
                    <td className="tabela__nome">
                      {p.nome}
                      {p.medicinaOcupacional && <span className="tabela__apoio"> · ocupacional</span>}
                    </td>
                    <td className="mono">{p.sigla || '—'}</td>
                    <td>{nomeEspecialidade(p.especialidadeId)}</td>
                    <td>{p.tipoLaudo === 'OIT_2000' ? 'OIT 2000' : 'Texto'}</td>
                    {podeVerValores && <td className="tabela--num mono">{dinheiro(p.valor)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </Lista>
        </div>
      </Bancada>
    </Layout>
  );
}
